import type { EventContentArg } from '@fullcalendar/core'
import { MessageCircleMore, NotebookPen } from 'lucide-react'

import type {
  AppointmentWithRelations,
  BlockedSlotWithStaff,
  ClinicScheduleDay,
} from '@snootsvet/shared'
import { formatName, toWallClockDate, toWallClockTime, wallClockToUtcIso } from '@snootsvet/shared'

import {
  getCategoryStyle,
  getDisplayStatus,
  getStatusStyle,
} from '@/frontend/features/appointments/appointments.utils'
export const calendarViewTypes = ['day', 'week'] as const
export type CalendarViewType = (typeof calendarViewTypes)[number]

/** Maps our view types to FullCalendar's internal view names. */
export const CALENDAR_VIEWS: Record<CalendarViewType, string> = {
  day: 'resourceTimeGridDay',
  week: 'timeGridWeek',
}

/**
 * Formats a human-readable title for the calendar toolbar, matching FullCalendar's
 * default title style for each view type (e.g. "15 June 2026" for day, "9 – 15 Jun 2026" for week).
 */
export function formatCalendarTitle(
  from: Date,
  to: Date,
  viewType: CalendarViewType,
  timeZone: string
): string {
  const locale = timeZone === 'Europe/London' ? 'en-GB' : 'en-US'

  if (viewType === 'day') {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone,
    }).format(from)
  }

  // The week's end is exclusive, so the last visible day is one day earlier.
  const lastDay = new Date(to.getTime() - 86_400_000)
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone,
  }).formatRange(from, lastDay)
}

/**
 * Maps an AppointmentWithRelations to a FullCalendar event object.
 * Background colour encodes appointment category; left-border stripes encode status.
 */
export function toCalendarEvent(appointment: AppointmentWithRelations) {
  const status = getDisplayStatus(appointment)
  const categoryColors = getCategoryStyle(appointment.appointmentTypeId)
  const { eventColors } = getStatusStyle(status)
  const { bg, border, text } = eventColors ?? categoryColors
  const title = appointment.patient.name

  const past = new Date(appointment.endTime) < new Date()

  return {
    id: appointment.id,
    title,
    start: appointment.startTime,
    end: appointment.endTime,
    backgroundColor: bg,
    borderColor: border,
    textColor: text,
    classNames: [`ev-s-${status}`, ...(past ? ['ev-past'] : [])],
    resourceId: appointment.staffId,
    ...(past ? { editable: false } : {}),
    extendedProps: { appointment },
  }
}

/**
 * Maps a BlockedSlotWithStaff to a FullCalendar event object with grey styling.
 * Stores type and block in extendedProps so the eventClick handler can distinguish
 * block events from appointment events.
 */
export function toBlockCalendarEvent(block: BlockedSlotWithStaff) {
  return {
    id: `block-${block.id}`,
    title: block.note ?? 'Blocked',
    start: block.startTime,
    end: block.endTime,
    backgroundColor: '#f3f4f6',
    borderColor: '#9ca3af',
    textColor: '#6b7280',
    resourceId: block.staff.id,
    extendedProps: { type: 'block' as const, block },
  }
}

/**
 * Calculates positioning for a hover card anchored to a calendar event element.
 * Anchors to the top-right of the visible portion of the element, clamping against
 * the nearest scroll container (e.g. the FC timegrid) so tall events that extend
 * above the scroll area still anchor correctly.
 */
export function calcHoverCardPosition(
  anchorEl: HTMLElement,
  tooltipEl: HTMLElement
): { top: number; left: number } {
  const anchor = anchorEl.getBoundingClientRect()
  const tooltipRect = tooltipEl.getBoundingClientRect()

  // Find the nearest scrollable ancestor to clamp against its visible bounds
  const scrollParent = findScrollParent(anchorEl)
  const clipTop = scrollParent ? scrollParent.getBoundingClientRect().top : 0

  const visibleTop = Math.max(anchor.top, clipTop)
  let top = visibleTop
  /** Pixel gap between the hover card and its anchor / viewport edges. */
  const HOVER_CARD_GAP = 8

  let left = anchor.right + HOVER_CARD_GAP

  if (left + tooltipRect.width > window.innerWidth - HOVER_CARD_GAP) {
    left = anchor.left - tooltipRect.width - HOVER_CARD_GAP
  }

  left = Math.max(HOVER_CARD_GAP, left)

  top = Math.max(
    HOVER_CARD_GAP,
    Math.min(top, window.innerHeight - tooltipRect.height - HOVER_CARD_GAP)
  )

  return { top, left }
}

const SCHEDULE_BLOCK_CLASSES: Record<string, string> = {
  'First/Sick': '[--schedule-color:245,158,11]',
  'First/Sick/Dental': '[--schedule-color:245,158,11]',
  Neuters: '[--schedule-color:168,85,247]',
  Anything: '[--schedule-color:34,197,94]',
  'Book On Day': '[--schedule-color:59,130,246]',
}

/** Boundaries that match the calendar's slotMinTime / slotMaxTime (HH:MM). */
export const SLOT_START = '07:00'
export const SLOT_END = '21:00'

/** The top of the hour before the clinic's current time, so the current hour opens with context above it. */
export function getDefaultScrollTime(timeZone: string) {
  const hour = Number(toWallClockTime(new Date(), timeZone).slice(0, 2))
  return `${String(Math.max(0, hour - 1)).padStart(2, '0')}:00`
}

function makeOffEvent(
  dateStr: string,
  start: string,
  end: string,
  timeZone: string,
  resourceId?: string
) {
  return {
    title: '',
    start: wallClockToUtcIso(dateStr, start, timeZone),
    end: wallClockToUtcIso(dateStr, end, timeZone),
    display: 'background' as const,
    classNames: ['ev-sched-off'],
    ...(resourceId ? { resourceId } : {}),
  }
}

/**
 * Builds FullCalendar background events from the fetched clinic schedule for a
 * single staff member. Returns coloured blocks for each schedule period and grey
 * "off" blocks for non-working time (before, between, and after schedule blocks,
 * and for days when the vet doesn't work at all).
 *
 * @param scheduleDays - Per-day working-vet view fetched via `useClinicSchedule`.
 * @param resourceId - When provided, pins each background event to a specific
 *   resource column (the vet's staff ID). Used in resource views so each vet's
 *   schedule appears in their own column. Omit for non-resource views.
 */
export function getScheduleBackgroundEvents(
  scheduleDays: ClinicScheduleDay[],
  staffEmail: string,
  timeZone: string,
  resourceId?: string
) {
  const events: {
    title: string
    start: string
    end: string
    display: 'background'
    classNames: string[]
    resourceId?: string
  }[] = []

  for (const { date: dateStr, vets } of scheduleDays) {
    const vetEntry = vets.find((d) => d.staff.email === staffEmail)

    if (!vetEntry) {
      // Vet doesn't work this day — grey out the entire visible slot range
      events.push(makeOffEvent(dateStr, SLOT_START, SLOT_END, timeZone, resourceId))
      continue
    }

    // Sort blocks chronologically, then fill gaps with grey "off" events
    const sorted = [...vetEntry.schedule].sort((a, b) =>
      a.startTime < b.startTime ? -1 : a.startTime > b.startTime ? 1 : 0
    )

    let prevEnd = SLOT_START
    for (const block of sorted) {
      if (block.startTime > prevEnd) {
        events.push(makeOffEvent(dateStr, prevEnd, block.startTime, timeZone, resourceId))
      }
      events.push({
        title: `${block.name} ${block.startTime}-${block.endTime}`,
        start: wallClockToUtcIso(dateStr, block.startTime, timeZone),
        end: wallClockToUtcIso(dateStr, block.endTime, timeZone),
        display: 'background',
        classNames: [SCHEDULE_BLOCK_CLASSES[block.name] ?? '[--schedule-color:148,163,184]'],
        ...(resourceId ? { resourceId } : {}),
      })
      prevEnd = block.endTime
    }

    // Grey out time after the last block
    if (prevEnd < SLOT_END) {
      events.push(makeOffEvent(dateStr, prevEnd, SLOT_END, timeZone, resourceId))
    }
  }

  return events
}

/** Returns whether a blocked slot covers every scheduled interval for a vet on a clinic date. */
export function scheduleIsFullyBlocked(
  block: BlockedSlotWithStaff,
  day: ClinicScheduleDay,
  timeZone: string
) {
  const schedule = day.vets.find(({ staff }) => staff.email === block.staff.email)?.schedule
  if (!schedule?.length) return false

  return schedule.every(
    ({ startTime, endTime }) =>
      Date.parse(block.startTime) <= Date.parse(wallClockToUtcIso(day.date, startTime, timeZone)) &&
      Date.parse(block.endTime) >= Date.parse(wallClockToUtcIso(day.date, endTime, timeZone))
  )
}

/**
 * Temporary week-view compatibility for legacy blocked-slot day-off records. Remove this helper and its week-view call once staff use schedule overrides for full days off instead of blocked slots.
 */
export function filterFullDayScheduleBlocks(
  scheduleDays: ClinicScheduleDay[],
  blocks: BlockedSlotWithStaff[],
  timeZone: string
) {
  const fullDayBlockIds = new Set(
    blocks
      .filter((block) => scheduleDays.some((day) => scheduleIsFullyBlocked(block, day, timeZone)))
      .map((block) => block.id)
  )
  const fullDayStaffByDate = new Set(
    scheduleDays.flatMap((day) =>
      day.vets
        .filter(({ staff }) =>
          blocks.some(
            (block) =>
              fullDayBlockIds.has(block.id) &&
              block.staff.email === staff.email &&
              scheduleIsFullyBlocked(block, day, timeZone)
          )
        )
        .map(({ staff }) => `${day.date}:${staff.email}`)
    )
  )

  return {
    scheduleDays: scheduleDays.map((day) => ({
      ...day,
      vets: day.vets.filter(({ staff }) => !fullDayStaffByDate.has(`${day.date}:${staff.email}`)),
    })),
    blocks: blocks.filter((block) => !fullDayBlockIds.has(block.id)),
  }
}

/**
 * Combines selected staff schedules into a week-view background underlay.
 * Overlapping blocks use Anything when present, their shared label when names
 * agree, or Mixed otherwise. Labels are compared case-insensitively with outer
 * whitespace trimmed. Adjacent segments with the same resulting label coalesce;
 * gaps remain unshaded. Styling reflects block names, independently of eligibility rules.
 */
export function getMergedScheduleBackgroundEvents(
  scheduleDays: ClinicScheduleDay[],
  staffEmails: string[],
  timeZone: string
) {
  return scheduleDays.flatMap(({ date, vets }) => {
    const blocks = vets
      .filter(({ staff }) => staffEmails.includes(staff.email))
      .flatMap(({ schedule }) => schedule)
    const boundaries = [...new Set(blocks.flatMap((b) => [b.startTime, b.endTime]))].sort()
    const segments: { name: string; startTime: string; endTime: string }[] = []

    for (let i = 0; i < boundaries.length - 1; i++) {
      const startTime = boundaries[i]!
      const endTime = boundaries[i + 1]!
      const active = blocks.filter((b) => b.startTime <= startTime && b.endTime >= endTime)
      if (!active.length) continue
      const names = [...new Set(active.map((b) => b.name.trim().toLowerCase()))]
      const name = names.includes('anything')
        ? 'Anything'
        : names.length === 1
          ? active[0]!.name.trim()
          : 'Mixed'
      const previous = segments.at(-1)
      if (previous?.name === name && previous.endTime === startTime) {
        previous.endTime = endTime
      } else {
        segments.push({ name, startTime, endTime })
      }
    }

    return segments.map(({ name, startTime, endTime }) => ({
      title: `${name} ${startTime}-${endTime}`,
      start: wallClockToUtcIso(date, startTime, timeZone),
      end: wallClockToUtcIso(date, endTime, timeZone),
      display: 'background' as const,
      classNames: [SCHEDULE_BLOCK_CLASSES[name] ?? '[--schedule-color:148,163,184]'],
    }))
  })
}

/**
 * Custom event content renderer — shows visit reason and notes below the
 * default title when the event block has enough vertical space.
 */
export function renderEventContent(arg: EventContentArg) {
  if (arg.event.display === 'background') {
    return (
      <div className="h-full overflow-hidden p-1 [writing-mode:vertical-lr] text-[0.65rem] font-medium italic leading-tight text-muted-foreground">
        {arg.event.title}
      </div>
    )
  }

  if (arg.event.extendedProps.type === 'block') {
    const block = arg.event.extendedProps.block as {
      staff: { firstName: string; lastName: string }
    }
    return (
      <div className="min-h-0 overflow-hidden">
        <div className="flex flex-wrap items-baseline gap-x-1.5">
          <span className="text-[0.7rem] leading-tight opacity-75">{arg.timeText}</span>
          <span className="text-xs font-medium leading-tight">{arg.event.title}</span>
        </div>
        <div className="shrink-0 truncate text-2xs leading-tight opacity-70">
          {formatName(block.staff)}
        </div>
      </div>
    )
  }

  const appointment = arg.event.extendedProps.appointment as
    | {
        appointmentTypeId: string
        appointmentTypeName?: string | null
        visitReason?: string | null
        userNotes?: string | null
      }
    | undefined

  const durationMins =
    arg.event.end && arg.event.start
      ? (arg.event.end.getTime() - arg.event.start.getTime()) / 60_000
      : 0

  const CategoryIcon = appointment ? getCategoryStyle(appointment.appointmentTypeId).icon : null

  return (
    <div className="relative min-h-0 overflow-hidden">
      <div className="flex flex-wrap items-baseline gap-x-1.5">
        <span className="text-[0.7rem] leading-tight opacity-75">{arg.timeText}</span>
        <span className="text-xs font-medium leading-tight">{arg.event.title}</span>
      </div>
      {arg.event.extendedProps.isAvailabilitySlot && (
        <div className="mb-0.5 shrink-0 truncate text-2xs leading-tight opacity-70">
          {arg.event.extendedProps.availabilityLabel}
        </div>
      )}
      {durationMins >= 15 && appointment?.appointmentTypeName && (
        <div className="mb-0.5 flex shrink-0 items-center gap-0.5 truncate text-2xs leading-tight opacity-70">
          {CategoryIcon && <CategoryIcon className="size-[0.6rem] shrink-0" />}
          {appointment.appointmentTypeName}
        </div>
      )}
      {durationMins >= 30 && appointment?.visitReason && (
        <div className="flex shrink-0 items-center text-2xs leading-tight italic opacity-70">
          <NotebookPen className="mr-0.5 size-[0.6rem] shrink-0" />
          <span className="truncate">{appointment.visitReason}</span>
        </div>
      )}
      {durationMins >= 30 && appointment?.userNotes && (
        <div className="flex shrink-0 items-center text-2xs leading-tight italic opacity-70">
          <MessageCircleMore className="mr-0.5 size-[0.6rem] shrink-0" />
          <span className="truncate">{appointment.userNotes}</span>
        </div>
      )}
    </div>
  )
}

/**
 * Converts FullCalendar's exclusive date range to wall-clock date strings
 * in the given timezone. Both `from` and `to` are exclusive upper/lower bounds
 * matching the API convention (e.g. Mon 00:00 → next Mon 00:00 in the clinic's
 * timezone becomes `from: "2026-06-01", to: "2026-06-08"`).
 */
export function toApiDateRange(
  fcStart: Date,
  fcEnd: Date,
  timeZone: string
): { from: string; to: string } {
  return {
    from: toWallClockDate(fcStart, timeZone),
    to: toWallClockDate(fcEnd, timeZone),
  }
}

/**
 * Formats a Date as "YYYY-MM-DD" using its *local* calendar components.
 *
 * The date picker (react-day-picker) yields a Date at the browser's local
 * midnight for the clicked day. FullCalendar's `gotoDate` interprets a JS Date
 * as an absolute instant resolved in the calendar's configured (clinic)
 * timezone, so when the browser timezone differs from the clinic timezone that
 * instant can land on a neighbouring day. Passing the calendar day as a plain
 * date string instead makes FullCalendar navigate to that exact day regardless
 * of timezone.
 */
export function toLocalDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Walks up the DOM to find the nearest ancestor with vertical overflow scrolling. */
function findScrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement
  while (node) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') return node
    node = node.parentElement
  }
  return null
}
